import React from "react";
import { Box, Modal } from "@mui/material";
import Container from "@/components/common/elements/Container";
import Text from "@/components/common/elements/Text";
import Button from "@/components/common/elements/Button";
import CloseIcon from "@/assets/icons/CloseIcon";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { designSystem } from "@/styles/theme";

// 모달 문구를 문장 단위로 줄바꿈: 한국어 문장 끝(다./요. 등 + 공백) 뒤에 개행 삽입.
// 이미 들어있는 의도적 개행(\n, \n\n)은 공백만 매칭하므로 보존된다.
const breakSentences = (text?: string): string | undefined =>
    typeof text === "string" ? text.replace(/([다요][.!?]) +/g, "$1\n") : text;

interface ResultModalProps {
    title: string;
    description?: string;
    open: boolean;
    onClose: () => void;
    buttoncolor?: string;
    buttontext?: string;
    onClick?: () => void;
    height?: string;
    status: boolean;
    /**
     * 함께 둘 주 동작(예: 환불 실패 → [문의하기]). 있으면 [buttontext(회색)] [action(청록)] 두 개를 한 줄에 둔다 —
     * 세미나 취소 팝업의 [닫기] [취소하고 환불받기] 와 같은 배치.
     */
    action?: { text: string; onClick: () => void };
}

const ResultModal = ({ title, description, open, onClose, buttoncolor, buttontext = "확인", onClick, height, status, action }: ResultModalProps) => {
    const style = {
        position: "absolute",
        top: "47vh",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: "450px",
        maxWidth: "calc(100vw - 2rem)",
        height: height ?? "auto",
        borderRadius: 5,
        overflowX: "hidden",
        p: "0 13px",
        zIndex: 9999,
        boxSizing: "border-box",
        // MUI Modal이 컨텐츠 Box에 tabindex=-1을 넣어 포커스 시 브라우저 기본 파란 outline이 뜨는 것 제거
        outline: "none",
        "&:focus, &:focus-visible": { outline: "none" }
    };

    return <Modal sx={{ border: "none", wordWrap: "break-word" }} open={open} onClose={onClose} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
        <Box sx={{ ...style, bgcolor: "#fff" }}>
            <CloseIcon onClick={onClose} position={{ top: '10px', right: '10px' }} />
            <Container display={"grid"} placecontent={"center"} justifyitems="center" margin={"clamp(20px, 5vw, 39px) 0 clamp(20px, 4vw, 33px)"}>
                {status ? (
                    <CheckCircleOutlineIcon
                        sx={{
                            fontSize: 'clamp(64px, 12vw, 80px)',
                            color: designSystem.colors.primary,
                        }}
                    />
                ) : (
                    <ErrorOutlineIcon
                        sx={{
                            fontSize: 'clamp(64px, 12vw, 80px)',
                            color: designSystem.colors.status.error,
                        }}
                    />
                )}
                <Text size={"clamp(16px, 3vw, 20px)"} fontweight={"500"} margin={"clamp(12px, 2.5vw, 19px) 0 0"} style={{ whiteSpace: 'pre-line', textAlign: 'center', width: '100%', wordBreak: 'break-word' }}>{breakSentences(title)}</Text>
                <Text textcolor={"#707070"} fontweight={"300"} size={"clamp(12px, 2vw, 13px)"} style={{ whiteSpace: 'pre-line', textAlign: 'center', width: '100%', wordBreak: 'break-word' }}>{breakSentences(description)}</Text>
            </Container>
            {action ? (
                <div style={{ display: "flex", gap: 10, margin: "0 0 16px" }}>
                    <Button bgcolor="#e5e7eb" color="#666" textalign="center" fontweight="600" style={{ flex: 1 }} onClick={onClick ?? onClose}>
                        {buttontext}
                    </Button>
                    <Button bgcolor={designSystem.colors.primary} color="#ffffff" textalign="center" fontweight="600" style={{ flex: 1 }} onClick={action.onClick}>
                        {action.text}
                    </Button>
                </div>
            ) :
                buttontext &&
                <Button
                    bgcolor={buttoncolor ?? designSystem.colors.primary}
                    color={buttoncolor ? undefined : "#ffffff"}
                    textalign="center"
                    fontweight="600"
                    margin="0 0 16px"
                    onClick={onClick ?? onClose}
                >
                    {buttontext}
                </Button>
            }
        </Box>
    </Modal>;
};

export default ResultModal;
